import { Code, ConnectError, type Interceptor } from '@connectrpc/connect'
import { getSessionToken, isSessionTokenExpired, refresh } from '@descope/react-sdk'

// Descope keeps the session token fresh via a background timer, but that timer misses when a tab is
// backgrounded/throttled or the machine sleeps — leaving getSessionToken() returning an expired JWT
// that the gateway rejects with 401. This interceptor closes that gap: it refreshes proactively when
// the token is already expired, and reactively (once) if a 401 slips through anyway. (ENG-3820)

// Dedupe concurrent refreshes so a burst of parallel requests shares ONE refresh() call — important
// because Descope may rotate the refresh token, so overlapping refreshes could invalidate each other.
let inflight: Promise<unknown> | null = null
const refreshOnce = () => (inflight ??= refresh().finally(() => (inflight = null)))

const isUnauthenticated = (e: unknown): boolean =>
  e instanceof ConnectError && e.code === Code.Unauthenticated

export const sessionTokenInterceptor: Interceptor = (next) => async (req) => {
  // Proactive: a known-expired token can't succeed — refresh before we send it.
  if (isSessionTokenExpired()) {
    try {
      await refreshOnce()
    } catch {
      // Fall through with whatever we have; the request may still 401 and be handled below.
    }
  }

  const tokenAtSend = getSessionToken()
  req.header.set('Authorization', `Bearer ${tokenAtSend}`)

  try {
    return await next(req)
  } catch (err) {
    // Reactive safety net: a 401 we didn't pre-empt (server-side expiry / clock skew). Streaming
    // requests can't be safely replayed, so leave them untouched.
    if (!isUnauthenticated(err) || req.stream) throw err

    try {
      await refreshOnce()
    } catch {
      throw err
    }

    const fresh = getSessionToken()
    // If the token didn't advance, the refresh didn't help (e.g. dead refresh token) — fail fast
    // rather than replaying into another 401.
    if (!fresh || fresh === tokenAtSend) throw err

    req.header.set('Authorization', `Bearer ${fresh}`)
    // Replay EXACTLY once. A second 401 propagates; retryLogic treats it as non-retryable.
    return await next(req)
  }
}
